import React, {useState, useEffect} from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import Icon from './icons/Icon';
import * as vfsClient from '../utils/vfsClient';
import { validateServiceName } from '../utils/gitRepoSource';
import './CreateServiceModal.css';

/**
 * Create Service Modal Component
 * Creates a plain, empty service folder directly in vfs-service — no
 * template gallery, no git-import option. Templates added unnecessary
 * friction (loading a marketplace, picking a version) for something that's
 * just "make this folder exist"; the agent or the user writes real files
 * into it afterward.
 */
const CreateServiceModal = ({
  isOpen,
  onClose,
  onCreate,
  projectId,
  takenNames = []
}) => {
  const [serviceName, setServiceName] = useState('');
  const [serviceDescription, setServiceDescription] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [error, setError] = useState(null);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setServiceName('');
      setServiceDescription('');
      setError(null);
    }
  }, [isOpen]);

  const handleCreate = async () => {
    const problem = validateServiceName(serviceName, takenNames);
    if (problem) {
      setError(problem);
      return;
    }

    setCreateLoading(true);
    setError(null);
    try {
      const trimmedName = serviceName.trim();
      // Every real service lives under its own subfolder of `services/`,
      // sibling to `.mia/project.json` and any project-level thumbnail — see
      // Canvas.jsx's loadConfiguration. `vfsServiceName` (the value used
      // everywhere as this service's real identity: file paths, the
      // `x-mia-vfs-service` header, the preview build's vfsSource) is the
      // FULL subfolder path, not just the user-typed short name.
      const vfsServiceName = `services/${trimmedName}`;

      await vfsClient.createProject(projectId);
      // Writing a placeholder file materializes the folder in vfs-service
      // (a plain filesystem — there's no separate "create directory" call,
      // parent dirs are created implicitly on write, same as every other
      // nested-path write in this codebase).
      await vfsClient.writeFile(projectId, `${vfsServiceName}/.gitkeep`, '');

      const serviceData = {
        name: vfsServiceName,
        displayName: trimmedName,
        description: serviceDescription.trim() || undefined,
        vfsBacked: true,
      };
      // filesByPath keys are service-relative (Canvas.jsx's handleCreateService
      // seeds these straight into IndexedDB under this service's name).
      if (onCreate) {
        onCreate(serviceData, {'.gitkeep': ''});
      }
      onClose();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error creating service:', err);
      setError(err.message || 'Failed to create service');
    } finally {
      setCreateLoading(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content create-service-modal" onClick={(e) => e.stopPropagation()}>
        {createLoading && (
          <div className="hexagonal-loader-container">
            <div className="hexagonal-loader">
              <div className="hexagon"></div>
              <div className="hexagon"></div>
              <div className="hexagon"></div>
              <div className="hexagon"></div>
              <div className="hexagon"></div>
              <div className="hexagon"></div>
            </div>
          </div>
        )}
        <div className="modal-header">
          <h2>Create New Service</h2>
          <button
            className="modal-close-btn"
            onClick={onClose}
            disabled={createLoading}
            type="button"
          >
            <Icon name="x" size={24} />
          </button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="error-message">
              <Icon name="alert-circle" size={16} />
              <span>{error}</span>
            </div>
          )}

          <div className="form-row-inline">
            <div className="form-group form-group-name">
              <label htmlFor="serviceName">Service Name *</label>
              <input
                id="serviceName"
                type="text"
                value={serviceName}
                onChange={(e) => setServiceName(e.target.value)}
                placeholder="my-service"
                disabled={createLoading}
                autoFocus
              />
            </div>
            <div className="form-group form-group-description">
              <label htmlFor="serviceDescription">Description</label>
              <input
                id="serviceDescription"
                type="text"
                value={serviceDescription}
                onChange={(e) => setServiceDescription(e.target.value)}
                placeholder="Service description..."
                disabled={createLoading}
              />
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button
            className="btn-secondary"
            onClick={onClose}
            disabled={createLoading}
            type="button"
          >
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={handleCreate}
            disabled={createLoading || !serviceName.trim()}
            type="button"
          >
            {createLoading ? (
              <>
                <Icon name="spinner" size={18} />
                <span>Creating...</span>
              </>
            ) : (
              <>
                <Icon name="plus" size={18} />
                <span>Create Service</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

CreateServiceModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func,
  projectId: PropTypes.string,
  // Short names of the services the project has: a new one never takes one of them.
  takenNames: PropTypes.arrayOf(PropTypes.string)
};

export default CreateServiceModal;
